import { DateTime } from 'luxon';

import {
  DASHBOARD_PERIOD_TIME_ZONE,
  type MissedEarningsTrip,
} from '@/api/partners/dashboard/use-dashboard-performance';
import { formatIsoInZone } from '@/core';

const DASHBOARD_TRIP_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const CHART_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function chartMonthLabel(month: string) {
  const [year, monthNumber] = month.split('-');
  const label = CHART_MONTH_NAMES[Number(monthNumber) - 1] ?? month;

  return { year, monthNumber, label };
}

export function formatDashboardTripDate(
  isoDate: string,
  timeZone: string = DASHBOARD_PERIOD_TIME_ZONE
) {
  if (DASHBOARD_TRIP_DATE_ONLY.test(isoDate)) {
    return DateTime.fromISO(isoDate, { zone: timeZone }).toFormat(
      'MMM d, yyyy'
    );
  }

  return formatIsoInZone(isoDate, timeZone, 'MMM d, yyyy');
}

export function formatMissedEarningsCarLabel(trip: MissedEarningsTrip) {
  const formattedName = trip.carName?.trim();

  if (formattedName) {
    return formattedName;
  }

  if (trip.brandName && trip.modelName) {
    const yearPrefix = trip.vehicleYear != null ? `${trip.vehicleYear} ` : '';
    const plate = trip.licensePlate?.trim();

    return `${yearPrefix}${trip.brandName} ${trip.modelName}${
      plate ? ` (${plate})` : ''
    }`.trim();
  }

  return '';
}

export const DASHBOARD_RATING_CRITICAL_THRESHOLD = 3;
export const DASHBOARD_RATING_BREAKDOWN_GOOD_THRESHOLD = 4;

export function formatDashboardRatingScore(score: number | null) {
  if (score === null) {
    return '—';
  }

  return score.toFixed(1);
}

export function formatChartMonth(month: string) {
  const { year, monthNumber, label } = chartMonthLabel(month);

  return monthNumber === '01' ? `${label} ${year}` : label;
}

export function formatChartMonthWithYear(month: string) {
  const { year, label } = chartMonthLabel(month);

  return `${label} ${year}`;
}

export function formatCompactLineChartLabel(month: string, index: number) {
  if (index === 0) {
    return formatChartMonthWithYear(month);
  }

  return formatChartMonth(month);
}

/** Integer percents that sum to 100 (largest remainder), for share legends. */
export function integerSharePercents(values: number[]) {
  const total = values.reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    return values.map(() => 0);
  }

  const exactPercents = values.map((value) => (value / total) * 100);
  const floored = exactPercents.map((percent) => Math.floor(percent));
  let remainder = 100 - floored.reduce((sum, percent) => sum + percent, 0);

  const byFraction = exactPercents
    .map((percent, index) => ({
      index,
      fraction: percent - Math.floor(percent),
    }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const percents = [...floored];

  for (let i = 0; i < remainder; i++) {
    percents[byFraction[i].index] += 1;
  }

  return percents;
}
